import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Users, Mail, Phone, Home } from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../utils/api';
import { formatDate } from '../utils/date';
import { toast } from 'sonner';

const Confirmation = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const bookingResponse = await api.get(`/bookings/${bookingId}`);
      setBooking(bookingResponse.data);
      
      const roomResponse = await api.get(`/rooms/${bookingResponse.data.room_id}`);
      setRoom(roomResponse.data);
    } catch (error) {
      toast.error('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9F8F6' }}>
        <p className="text-lg" style={{ color: '#64748B' }}>Loading...</p>
      </div>
    );
  }

  if (!booking || !room) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9F8F6' }}>
        <div className="text-center">
          <p className="text-lg mb-4" style={{ color: '#64748B' }}>Booking not found</p>
          <Link to="/rooms">
            <button className="btn-primary">Browse Rooms</button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F9F8F6' }}>
      <Navbar scrolled={scrolled} />

      <div className="pt-32 pb-24 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6"
              style={{ background: '#8F9E8B' }}
            >
              <CheckCircle size={48} style={{ color: 'white' }} />
            </motion.div>
            <h1
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
              data-testid="confirmation-title"
            >
              Booking Confirmed!
            </h1>
            <p className="text-lg" style={{ color: '#64748B' }}>
              Thank you for choosing The BaghHome. We've sent a confirmation email to {booking.guest_email}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white rounded-3xl p-8 shadow-sm mb-8"
            data-testid="booking-details"
          >
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}>
              Booking Details
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <img
                  src={room.image_url}
                  alt={room.name}
                  className="w-full h-48 object-cover rounded-2xl"
                />
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm" style={{ color: '#64748B' }}>Booking ID</p>
                  <p className="font-bold mono" style={{ color: '#1A3C34' }}>{booking.id}</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: '#64748B' }}>Room</p>
                  <p className="font-bold" style={{ color: '#1A3C34' }}>{room.name}</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: '#64748B' }}>Total Amount</p>
                  <p className="text-2xl font-bold mono" style={{ color: '#1A3C34' }}>₹{booking.total_amount}</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 p-6 rounded-2xl" style={{ background: '#F9F8F6' }}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#1A3C34' }}>
                  <Calendar size={20} style={{ color: 'white' }} />
                </div>
                <div>
                  <p className="text-sm font-medium mb-1" style={{ color: '#64748B' }}>Check-in</p>
                  <p className="font-bold" style={{ color: '#1A202C' }}>{formatDate(booking.check_in)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#1A3C34' }}>
                  <Calendar size={20} style={{ color: 'white' }} />
                </div>
                <div>
                  <p className="text-sm font-medium mb-1" style={{ color: '#64748B' }}>Check-out</p>
                  <p className="font-bold" style={{ color: '#1A202C' }}>{formatDate(booking.check_out)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#1A3C34' }}>
                  <Users size={20} style={{ color: 'white' }} />
                </div>
                <div>
                  <p className="text-sm font-medium mb-1" style={{ color: '#64748B' }}>Guests</p>
                  <p className="font-bold" style={{ color: '#1A202C' }}>{booking.num_guests} {booking.num_guests === 1 ? 'Guest' : 'Guests'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#1A3C34' }}>
                  <Mail size={20} style={{ color: 'white' }} />
                </div>
                <div>
                  <p className="text-sm font-medium mb-1" style={{ color: '#64748B' }}>Email</p>
                  <p className="font-bold" style={{ color: '#1A202C' }}>{booking.guest_email}</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center"
          >
            <Link to="/">
              <button className="btn-primary" data-testid="back-home-button">
                <Home size={20} className="inline mr-2" />
                Back to Home
              </button>
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;